import type {
  FeedbackAnswer,
  FeedbackDiscussionComment,
  RichCommentBody,
} from '@nimbalyst/collab-protocol';
import {
  FeedbackRequestSync,
  FeedbackRequestSyncError,
  type FeedbackRequestServiceState,
  type FeedbackRequestSyncEvent,
  type FeedbackRespondHost,
  type FeedbackRespondSubmitResult,
} from '@nimbalyst/collab-bundle/feedback-ui';

import { consoleConfig } from '../config';
import {
  asTeamMemberId,
  getTeamSessionForOrg,
  TeamSessionError,
  type BrowserSessionClient,
  type TeamMemberId,
} from '../auth/teamJwt';

export interface BrowserFeedbackRequestTarget {
  orgId: string;
  requestId: string;
}

export type BrowserFeedbackRequestAccessFailure =
  | 'not-signed-in'
  | 'not-authorized';

/** Shared respond state plus the auth distinction needed by the route shell. */
export interface BrowserFeedbackRequestState extends FeedbackRequestServiceState {
  accessFailure?: BrowserFeedbackRequestAccessFailure;
}

/**
 * Stands in until the team JWT resolves and the real member id is known.
 * `feedbackRequestSurface` reads an empty id as "we cannot tell yet whether
 * this viewer is a recipient", which is the safe answer while unauthenticated.
 */
const UNRESOLVED_MEMBER_ID = asTeamMemberId('');

export interface BrowserFeedbackRequestClientOptions {
  target: BrowserFeedbackRequestTarget;
  session: BrowserSessionClient;
  expectedMemberId?: TeamMemberId | null;
  createWebSocket?: (url: string) => WebSocket;
  createMutationId?: () => string;
}

function defaultMutationId(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `feedback-respond-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function accessFailureForError(
  error: unknown,
): BrowserFeedbackRequestAccessFailure | undefined {
  if (!(error instanceof TeamSessionError)) return undefined;
  if (error.kind === 'session-expired' || error.kind === 'malformed-token') {
    return 'not-signed-in';
  }
  if (
    error.kind === 'organization-unavailable'
    || error.kind === 'wrong-org'
    || error.kind === 'member-mismatch'
  ) {
    return 'not-authorized';
  }
  return undefined;
}

function accessFailureForCode(
  code: string,
): BrowserFeedbackRequestAccessFailure | undefined {
  const normalized = code.toLowerCase();
  return normalized.includes('forbidden') || normalized.includes('unauthorized')
    ? 'not-authorized'
    : undefined;
}

function stateError(error: unknown): {
  accessFailure?: BrowserFeedbackRequestAccessFailure;
  error: { code: string; message: string };
} {
  const accessFailure = accessFailureForError(error);
  if (accessFailure) {
    return {
      accessFailure,
      error: {
        code: accessFailure === 'not-signed-in'
          ? 'FEEDBACK_REQUEST_NOT_SIGNED_IN'
          : 'FEEDBACK_REQUEST_NOT_AUTHORIZED',
        message: error instanceof Error
          ? error.message
          : 'This feedback request could not be opened.',
      },
    };
  }
  return {
    error: {
      code: error instanceof FeedbackRequestSyncError
        ? error.code
        : 'FEEDBACK_REQUEST_CONNECTION_FAILED',
      message: error instanceof Error
        ? error.message
        : 'This feedback request could not be opened.',
    },
  };
}

/**
 * Browser owner for one feedback request connection.
 *
 * The Durable Object remains authoritative: this client keeps only the current
 * in-memory projection and tears it down with the tab.
 */
export class BrowserFeedbackRequestClient {
  private readonly sync: FeedbackRequestSync;
  private readonly createMutationId: () => string;
  private readonly listeners = new Set<(
    state: BrowserFeedbackRequestState,
  ) => void>();
  private readonly unsubscribeSync: () => void;
  private state: BrowserFeedbackRequestState;
  private startPromise: Promise<BrowserFeedbackRequestState> | null = null;
  private terminalAccessFailure = false;
  private destroyed = false;

  constructor(options: BrowserFeedbackRequestClientOptions) {
    this.createMutationId = options.createMutationId ?? defaultMutationId;
    this.state = {
      workspacePath: '',
      orgId: options.target.orgId,
      requestId: options.target.requestId,
      teamMemberId: options.expectedMemberId ?? UNRESOLVED_MEMBER_ID,
      status: 'idle',
    };
    this.sync = new FeedbackRequestSync({
      serverUrl: consoleConfig.collabApiBaseUrl,
      target: options.target,
      getTeamJwt: async () => {
        const session = await getTeamSessionForOrg(
          options.session,
          options.target.orgId,
          consoleConfig.sessionDurationMinutes,
          options.expectedMemberId,
        );
        this.updateState({ teamMemberId: session.memberId });
        return session.jwt;
      },
      createWebSocket: options.createWebSocket,
    });
    this.unsubscribeSync = this.sync.subscribe((event) => {
      this.handleSyncEvent(event);
    });
  }

  getState(): BrowserFeedbackRequestState {
    return this.state;
  }

  subscribe(listener: (state: BrowserFeedbackRequestState) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  start(): Promise<BrowserFeedbackRequestState> {
    if (this.destroyed) {
      return Promise.reject(new Error('Browser feedback request client has been destroyed'));
    }
    if (this.terminalAccessFailure) return Promise.resolve(this.state);
    if (this.startPromise) return this.startPromise;
    this.startPromise = this.startConnection().finally(() => {
      this.startPromise = null;
    });
    return this.startPromise;
  }

  createRespondHost(): FeedbackRespondHost {
    return {
      submitAnswers: (answers) => this.submitAnswers(answers),
    };
  }

  /**
   * The discussion renderer's only write path.
   *
   * A passthrough rather than a wrapper on purpose: the caller mints the
   * mutation id and keeps it across a retry, which is what makes a resend
   * dedupe on the server instead of posting the same comment twice. Rejections
   * are left as thrown `FeedbackRequestSyncError`s so the caller can tell the
   * discussion byte budget apart from a dropped socket.
   */
  comment(
    clientMutationId: string,
    body: RichCommentBody,
  ): Promise<FeedbackDiscussionComment> {
    return this.sync.comment(clientMutationId, body);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.unsubscribeSync();
    this.sync.destroy();
    this.listeners.clear();
  }

  private async startConnection(): Promise<BrowserFeedbackRequestState> {
    try {
      await this.sync.connect();
      await this.sync.sync();
    } catch (error) {
      const failure = stateError(error);
      this.updateState({ status: 'error', ...failure });
      if (failure.accessFailure) {
        this.terminalAccessFailure = true;
        this.sync.destroy();
      }
    }
    return this.state;
  }

  private async submitAnswers(
    answers: Array<{ askId: string; answer: FeedbackAnswer }>,
  ): Promise<FeedbackRespondSubmitResult> {
    for (const { askId, answer } of answers) {
      try {
        await this.sync.respond(this.createMutationId(), askId, answer);
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error
            ? error.message
            : 'Your answers could not be sent.',
        };
      }
    }
    return { success: true };
  }

  private handleSyncEvent(event: FeedbackRequestSyncEvent): void {
    if (event.type === 'state') {
      this.updateState({
        status: 'connected',
        request: event.state.request,
        progress: event.state.progress,
        error: undefined,
        accessFailure: undefined,
      });
      return;
    }
    if (event.type === 'nudged') {
      this.updateState({ lastNudge: event.receipt });
      return;
    }
    if (event.type === 'error') {
      this.updateState({
        status: 'error',
        error: { code: event.code, message: event.message },
        accessFailure: this.state.request
          ? undefined
          : accessFailureForCode(event.code),
      });
      return;
    }
    if (event.type === 'disconnected' && !this.state.request) {
      this.updateState({
        status: 'error',
        error: {
          code: 'FEEDBACK_REQUEST_CONNECTION_CLOSED',
          message: 'Feedback request connection closed before the request completed',
        },
        accessFailure: undefined,
      });
      return;
    }
    this.updateState({
      status: event.type,
      ...(event.type === 'connecting' || event.type === 'connected'
        ? { error: undefined, accessFailure: undefined }
        : {}),
    });
  }

  private updateState(patch: Partial<BrowserFeedbackRequestState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }
}
